import { useState } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useTableSetting, type CoverState } from './useTableSetting'
import type { CutleryType } from './useCutlery'
import { useTimer } from '../useTimer'
import { CutleryIcon } from './CutlerySVGs'
import { s } from './cutleryStyles'
import GamePlayHeader from '../GamePlayHeader'

/* ── Table Play Screen ───────────────────────────────────────── */
export function TablePlayScreen({ state, onSelectItem, onPlaceInCover, onPlaceItemById, onBack }: {
  state:           ReturnType<typeof useTableSetting>['state']
  onSelectItem:    (id: string) => void
  onPlaceInCover:  (coverId: string) => void
  onPlaceItemById: (itemId: string, coverId: string) => void
  onBack:          () => void
}) {
  const { t, language } = useTranslation()
  const c               = t.cutlery
  const elapsed         = useTimer(state.startTime, state.endTime)
  const totalSlots      = state.covers.reduce((n, c) => n + c.order.required.length, 0)
  const placedSlots     = state.covers.reduce((n, c) => n + c.placed.length, 0)
  const hasSelected = state.selectedId !== null

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={c.modes.table.label}
        stats={[
          { value: state.moves, label: c.moves },
          { value: elapsed,     label: c.time },
        ]}
        progress={(placedSlots / totalSlots) * 100}
        progressNow={placedSlots}
        progressMax={totalSlots}
        onBack={onBack}
        backLabel={c.backToDashboard}
      />

      <main style={s.tableMain}>
        {/* Table */}
        <div style={s.tableCloth}>
          <div style={s.tableCovers}>
            {state.covers.map((cover) => (
              <TableCoverCard
                key={cover.order.id}
                cover={cover}
                language={language}
                hasSelection={hasSelected}
                isWrong={state.wrongCoverId === cover.order.id}
                onClickCover={() => hasSelected && onPlaceInCover(cover.order.id)}
                onDropItem={(itemId) => onPlaceItemById(itemId, cover.order.id)}
              />
            ))}
          </div>
        </div>

        <p style={s.instruction}>
          {hasSelected ? c.instructionPlaceTable : c.instructionPickTable}
        </p>

        {/* Pile */}
        <div style={s.pile}>
          {state.pile.map((item) => (
            <PileCard
              key={item.id}
              item={item}
              isSelected={state.selectedId === item.id}
              dimmed={hasSelected && state.selectedId !== item.id}
              onClick={() => onSelectItem(item.id)}
            />
          ))}
        </div>
      </main>
    </div>
  )
}

/* ── Table Cover Card ─────────────────────────────────────────── */
function TableCoverCard({ cover, language, hasSelection, isWrong, onClickCover, onDropItem }: {
  cover:        CoverState
  language:     string
  hasSelection: boolean
  isWrong:      boolean
  onClickCover: () => void
  onDropItem:   (itemId: string) => void
}) {
  const [dragOver, setDragOver] = useState(false)

  const borderColor = isWrong
    ? '#B5541A'
    : cover.complete
      ? '#52B788'
      : dragOver
        ? '#8B6347'
        : hasSelection
          ? '#C0A882'
          : '#D8C3A5'

  const boxShadow = cover.complete
    ? '0 0 0 3px #52B78860, 0 4px 16px rgba(82,183,136,0.25)'
    : dragOver
      ? '0 0 0 3px #8B634740, 0 4px 12px rgba(0,0,0,0.1)'
      : hasSelection
        ? '0 0 0 2px #C0A88240, 0 2px 8px rgba(0,0,0,0.08)'
        : '0 2px 8px rgba(0,0,0,0.06)'

  return (
    <div
      className={isWrong ? 'wm-shake' : ''}
      style={{ ...s.placemat, border: `2px solid ${borderColor}`, boxShadow, cursor: hasSelection && !cover.complete ? 'pointer' : 'default' }}
      onClick={cover.complete ? undefined : onClickCover}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        const id = e.dataTransfer.getData('text/plain')
        if (id && !cover.complete) onDropItem(id)
      }}
    >
      {/* Always-visible dish list */}
      <div style={s.coverDishList}>
        {cover.order.dishes.map((dish) => (
          <div key={dish.id} style={s.coverDishRow}>
            <span style={s.coverDishEmoji}>{dish.emoji}</span>
            <span style={s.coverDishName}>{language === 'es' ? dish.es : dish.en}</span>
          </div>
        ))}
      </div>

      {/* Plate area — relative container for cutlery layout */}
      <div style={s.plateArea}>
        {/* Teaspoon — above plate */}
        {cover.placed.includes('teaspoon') && (
          <div style={s.placedTeaspoon}>
            <CutleryIcon type="teaspoon" width={13} />
          </div>
        )}

        {/* Plate circle */}
        <div style={s.plate} />

        {/* Fork — left of plate */}
        {cover.placed.includes('fork') && (
          <div style={s.placedFork}>
            <CutleryIcon type="fork" width={22} />
          </div>
        )}

        {/* Spoon — right of knife */}
        {cover.placed.includes('spoon') && (
          <div style={s.placedSpoon}>
            <CutleryIcon type="spoon" width={22} />
          </div>
        )}

        {/* Knife — right of plate */}
        {cover.placed.includes('knife') && (
          <div style={s.placedKnife}>
            <CutleryIcon type="knife" width={22} />
          </div>
        )}

        {/* Complete badge */}
        {cover.complete && (
          <div style={s.completeBadge}>✓</div>
        )}
      </div>
    </div>
  )
}

/* ── Pile Card ────────────────────────────────────────────────── */
function PileCard({ item, isSelected, dimmed, onClick }: {
  item:       { id: string; type: CutleryType }
  isSelected: boolean
  dimmed:     boolean
  onClick:    () => void
}) {
  const [dragging, setDragging] = useState(false)

  return (
    <button
      draggable
      onClick={onClick}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', item.id)
        e.dataTransfer.effectAllowed = 'move'
        setDragging(true)
      }}
      onDragEnd={() => setDragging(false)}
      style={{
        ...s.pileCard,
        ...(isSelected ? s.pileCardSelected : {}),
        opacity:   dragging ? 0.35 : dimmed ? 0.45 : 1,
        transform: isSelected ? 'translateY(-8px) scale(1.08)' : 'translateY(0) scale(1)',
        cursor:    dragging ? 'grabbing' : 'grab',
      }}
      aria-pressed={isSelected}
    >
      <CutleryIcon type={item.type} width={28} />
    </button>
  )
}
